import React, { useState, useEffect, useCallback } from 'react';
import { Loader } from 'UI';
import { Button, Form, Space, Typography } from 'antd';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  AddressElement,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/mstore';
import { useModal } from 'Components/Modal';
import ENV from '../../../../env'

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true,
};

const options: StripeElementsOptions = {
  appearance: {
    theme: 'stripe',
    variables: {
      colorPrimary: '#394EFF',
      borderRadius: '3px',
      // fontFamily: '--body-font-family: -apple-system, BlinkMacSystemFont, sans-serif',
      colorBackground: '#fafafa'
    }
  },
  locale: 'auto'
};

interface PaymentFormProps {
  billingStore: any;
  plan: any;
  hideModal: () => void;
  submitButtonText: string;
  isLoading: boolean;
  addressOptions: any;
  handleAddressChange: (event: any) => void;
  isAddressComplete: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  billingStore,
  plan,
  hideModal,
  submitButtonText,
  isLoading,
  addressOptions,
  handleAddressChange,
  isAddressComplete,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isCardComplete, setIsCardComplete] = useState(false);

  const handleCardChange = (event: any) => setIsCardComplete(event.complete);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (billingStore.updating) return;
    if (!stripe || !elements) return;

    if (!plan.billing.last4 || isCardComplete) {
      const cardElement = elements.getElement(CardElement);
      if (cardElement) {
        const { error, token } = await stripe.createToken(cardElement);
        if (error) {
          toast.error(error.message);
          return;
        }
        plan.billing.edit({ cardToken: token.id });
      }
    }

    try {
      await billingStore.updatePlan(plan);
      hideModal();
      toast.success('Billing details updated successfully');
    } catch (error) {
      toast.error(error || 'An unexpected error occurred. Please try again.');
      console.error('Update plan error:', error);
    }
  };

  return (
    <Form onSubmitCapture={handleSubmit} layout="vertical" style={{ rowGap: '24px' }}>
      <Form.Item>
        <Space direction="vertical" className="w-full">
          <Typography.Text className="text-lg mb-2 font-medium">
            Card Details
          </Typography.Text>
          {plan.billing.last4 && (
            <div style={{ marginBottom: 8 }}>
              Current card on file ending in {plan.billing.last4}
            </div>
          )}
          <CardElement onChange={handleCardChange}
            options={CARD_ELEMENT_OPTIONS}
            className='border rounded px-2 py-3 bg-gray-lightest' />
        </Space>
      </Form.Item>
      <Form.Item>
        <Space direction="vertical" className="w-full">
          <Typography.Text className="text-lg mb-2 font-medium">
            Billing Address
          </Typography.Text>
          <AddressElement options={addressOptions} onChange={handleAddressChange} />
        </Space>
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          loading={isLoading}
          disabled={isLoading || !isAddressComplete || (!plan.billing.last4 && !isCardComplete)}
          onClick={handleSubmit}
        >
          {submitButtonText}
        </Button>
      </Form.Item>
    </Form>
  );
};

const PaymentInformation = () => {
  const { billingStore } = useStore();
  const plan = billingStore.instance;
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const { hideModal } = useModal();
  const [isAddressComplete, setIsAddressComplete] = useState(false);

  useEffect(() => {
    setStripePromise(loadStripe(ENV.STRIPE_KEY));
    if (!billingStore.planFetched) void billingStore.fetchPlan();
  }, [billingStore.planFetched]);

  const handleAddressChange = useCallback(
    (event: any) => {
      setIsAddressComplete(event.complete);
      if (event.complete && event.value) {
        if (event.value.address) {
          const { line1, line2, postal_code, city, state, country } = event.value.address;
          plan.billing.address.edit({ line1, line2, postal_code, city, state, country });
        }
        if (event.value.name) {
          plan.billing.edit({ name: event.value.name });
        }
      }
    },
    [plan.billing.address, plan.billing.name]
  );

  const isLoading = billingStore.updating;
  const submitButtonText = plan.billing.last4 ? 'Update' : 'Upgrade Plan';

  const addressOptions = {
    mode: 'billing',
    defaultValues: {
      name: plan.billing.name || '',
      address: {
        line1: plan.billing.address.line1 || '',
        line2: plan.billing.address.line2 || '',
        postal_code: plan.billing.address.postal_code || '',
        city: plan.billing.address.city || '',
        state: plan.billing.address.state || '',
        country: plan.billing.address.country || '',
      },
    },
  };

  return (
    <div className={'p-4 h-screen'}>
      <Loader loading={billingStore.loading}>
        <>
          <section className="mb-6">
            <Space direction="vertical" className="w-full">
              <Typography.Text className="text-lg mb-2 font-medium">
                Plan (Pay-as-you-go)
              </Typography.Text>
              <Typography.Text>
                $5.95 (per 1K sessions/month) - 30 day retention
              </Typography.Text>
            </Space>
          </section>
          {stripePromise && (
            <Elements stripe={stripePromise} options={options}>
              <section>
                <PaymentForm
                  billingStore={billingStore}
                  plan={plan}
                  hideModal={hideModal}
                  submitButtonText={submitButtonText}
                  isLoading={isLoading}
                  addressOptions={addressOptions}
                  handleAddressChange={handleAddressChange}
                  isAddressComplete={isAddressComplete}
                />
              </section>
            </Elements>
          )}
        </>
      </Loader>
    </div>
  );
};

export default observer(PaymentInformation);
